import { supabase } from "@/shared/lib/supabase";

export interface ScrapeExtractedData {
  firstName: string;
  surName: string;
  suffix?: string | null;
  middleName?: string | null;
  nickname?: string | null;
  dateOfBirth?: string | null;
  dateOfDeath?: string | null;
  funeralHome?: string | null;
  mainImageUrl?: string | null;
  address: {
    city?: string | null;
    state?: string | null;
  };
  family?: Record<string, string>;
}

export interface ScrapeResult {
  extractedData: ScrapeExtractedData;
}

export interface ScrapeCacheResult {
  status: "loading" | "done" | "error";
  data: ScrapeResult | null;
  error: string | null;
}

type CacheState =
  | { status: "idle" }
  | { status: "loading"; promise: Promise<ScrapeResult> }
  | { status: "done"; data: ScrapeResult }
  | { status: "error"; error: string };

const STORAGE_KEY = "onboarding_scrape_result";

function loadFromStorage(): CacheState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const data = JSON.parse(stored) as ScrapeResult;
      return { status: "done", data };
    }
  } catch {
    // fall through
  }
  return { status: "idle" };
}

let cache: CacheState = loadFromStorage();
let cachedUrl: string | null = null;

const TIMEOUT_MS = 15000;

export function startObitScrape(url: string): void {
  if (cache.status === "done") return;
  if (cachedUrl === url && cache.status !== "idle") return;

  cachedUrl = url;
  const promise = fetchScrape(url);
  cache = { status: "loading", promise };

  promise
    .then((data) => {
      cache = { status: "done", data };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    })
    .catch((err: Error) => {
      cache = { status: "error", error: err.message };
    });
}

export async function getObitScrapeResult(): Promise<ScrapeCacheResult> {
  if (cache.status === "idle") {
    return { status: "loading", data: null, error: null };
  }

  if (cache.status === "loading") {
    try {
      const data = await cache.promise;
      return { status: "done", data, error: null };
    } catch (err) {
      const message = err instanceof Error ? err.message : "Scraping failed";
      return { status: "error", data: null, error: message };
    }
  }

  if (cache.status === "done") {
    return { status: "done", data: cache.data, error: null };
  }

  return { status: "error", data: null, error: cache.error };
}

export function getObitScrapeStatus(): "idle" | "loading" | "done" | "error" {
  return cache.status;
}

export function resetObitScrapeCache(): void {
  cache = { status: "idle" };
  cachedUrl = null;
  localStorage.removeItem(STORAGE_KEY);
}

async function parseInvokeError(error: unknown): Promise<string> {
  try {
    if (error && typeof error === "object" && "context" in error) {
      const ctx = (error as { context: Response }).context;
      if (ctx instanceof Response) {
        const body = await ctx.json();
        if (typeof body?.error === "string") return body.error;
      }
    }
  } catch {
    // fall through
  }
  return "We couldn't read that obituary. Please enter the information manually.";
}

async function fetchScrape(url: string): Promise<ScrapeResult> {
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(
      () =>
        reject(
          new Error(
            "We couldn't process that link. Please check the URL or enter the details manually.",
          ),
        ),
      TIMEOUT_MS,
    );
  });

  const result = await Promise.race([
    supabase.functions.invoke<ScrapeResult>("onboarding/obituary/scrape", {
      body: { url },
    }),
    timeoutPromise,
  ]);

  if (result.error) {
    const message = await parseInvokeError(result.error);
    throw new Error(message);
  }

  if (!result.data) {
    throw new Error("No data was returned. Please enter the information manually.");
  }

  const { extractedData } = result.data;
  if (!extractedData.firstName && !extractedData.surName) {
    throw new Error("We couldn't find enough information. Please enter the details manually.");
  }

  return result.data;
}
